import React from "react";
import { X, type LucideIcon } from "lucide-react";
import { cn, formatNumber } from "@/lib/utils";

export type KpiVariant =
  | "default"
  | "rose"
  | "amber"
  | "emerald"
  | "blue"
  | "indigo"
  | "purple"
  | "slate";

export interface CompactKpiItem<T = string> {
  key: T;
  label: string;
  count: number | string;
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  variant?: KpiVariant;
  tooltip?: string;
  clickable?: boolean;
  animate?: boolean;
  hideWhenZero?: boolean;
  subLabel?: string;
  onClick?: () => void;
  testId?: string;
}

export interface CompactKpiRibbonProps<T = string> {
  items: CompactKpiItem<T>[];
  activeKey?: T | null;
  onSelect?: (key: T | null) => void;
  isLoading?: boolean;
  className?: string;
  extraActions?: React.ReactNode;
  testIdPrefix?: string;
}

export function CompactKpiRibbon<T = string>({
  items,
  activeKey,
  onSelect,
  isLoading = false,
  className,
  extraActions,
  testIdPrefix,
}: CompactKpiRibbonProps<T>) {
  return (
    <div
      data-testid="compact-kpi-ribbon"
      className={cn(
        "h-7 sm:h-8 flex items-center justify-between gap-2 w-full select-none text-xs",
        className
      )}
    >
      {/* Scrollable Single-Line Tag Track */}
      <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar py-0.5 px-0.5 min-w-0 flex-1">
        {items.map((item) => {
          const isItemClickable =
            item.clickable ?? (Boolean(item.onClick) || Boolean(onSelect));
          const isActive =
            activeKey !== null &&
            activeKey !== undefined &&
            activeKey === item.key;
          const Icon = item.icon;

          // Numeric count evaluation for zero-check
          const numCount =
            typeof item.count === "number"
              ? item.count
              : typeof item.count === "string"
                ? parseFloat(item.count.replace(/[^0-9.-]/g, ""))
                : NaN;
          const isZero = !isNaN(numCount) && numCount === 0;

          // Hide tag if hideWhenZero is enabled
          if (item.hideWhenZero && isZero) {
            return null;
          }

          const handleClick = () => {
            if (isLoading) return;
            if (item.onClick) {
              item.onClick();
              return;
            }
            if (onSelect) {
              onSelect(isActive ? null : item.key);
            }
          };

          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (isItemClickable && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              handleClick();
            }
          };

          // Format displayed count
          const displayCount =
            typeof item.count === "number"
              ? formatNumber(item.count)
              : item.count;

          // Compute style classes based on variant, state, and zero status
          const variant = item.variant || "default";

          // Alert Blindness Prevention: If count is 0 and not active, mute colors
          let pillColorClass = "";
          let iconColorClass = "";
          let badgeColorClass = "";

          if (isActive) {
            switch (variant) {
              case "rose":
                pillColorClass =
                  "ring-2 ring-rose-500 border-rose-400 bg-rose-100/90 text-rose-900 font-semibold shadow-xs";
                iconColorClass = "text-rose-600";
                badgeColorClass = "bg-white text-rose-800 border-rose-300";
                break;
              case "amber":
                pillColorClass =
                  "ring-2 ring-amber-500 border-amber-400 bg-amber-100/90 text-amber-900 font-semibold shadow-xs";
                iconColorClass = "text-amber-600";
                badgeColorClass = "bg-white text-amber-800 border-amber-300";
                break;
              case "emerald":
                pillColorClass =
                  "ring-2 ring-emerald-500 border-emerald-400 bg-emerald-100/90 text-emerald-900 font-semibold shadow-xs";
                iconColorClass = "text-emerald-600";
                badgeColorClass = "bg-white text-emerald-800 border-emerald-300";
                break;
              case "blue":
                pillColorClass =
                  "ring-2 ring-blue-500 border-blue-400 bg-blue-100/90 text-blue-900 font-semibold shadow-xs";
                iconColorClass = "text-blue-600";
                badgeColorClass = "bg-white text-blue-800 border-blue-300";
                break;
              case "indigo":
                pillColorClass =
                  "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-100/90 text-indigo-900 font-semibold shadow-xs";
                iconColorClass = "text-indigo-600";
                badgeColorClass = "bg-white text-indigo-800 border-indigo-300";
                break;
              case "purple":
                pillColorClass =
                  "ring-2 ring-purple-500 border-purple-400 bg-purple-100/90 text-purple-900 font-semibold shadow-xs";
                iconColorClass = "text-purple-600";
                badgeColorClass = "bg-white text-purple-800 border-purple-300";
                break;
              default:
                pillColorClass =
                  "ring-2 ring-primary border-primary/60 bg-primary/10 text-primary font-semibold shadow-xs";
                iconColorClass = "text-primary";
                badgeColorClass = "bg-white text-primary border-primary/30";
                break;
            }
          } else if (isZero && variant !== "default") {
            // Muted subtle state to prevent Alert Blindness
            pillColorClass =
              "bg-slate-50/80 border-slate-200/80 text-slate-400 hover:bg-slate-100/80 hover:text-slate-600 hover:border-slate-300";
            iconColorClass = "text-slate-400";
            badgeColorClass = "bg-white/80 text-slate-400 border-slate-200";
          } else {
            // Normal colorful idle state
            switch (variant) {
              case "rose":
                pillColorClass =
                  "bg-rose-50/80 border-rose-200/90 text-rose-700 hover:bg-rose-100/70 hover:border-rose-300";
                iconColorClass = "text-rose-600";
                badgeColorClass = "bg-white text-rose-700 border-rose-200";
                break;
              case "amber":
                pillColorClass =
                  "bg-amber-50/80 border-amber-200/90 text-amber-700 hover:bg-amber-100/70 hover:border-amber-300";
                iconColorClass = "text-amber-600";
                badgeColorClass = "bg-white text-amber-700 border-amber-200";
                break;
              case "emerald":
                pillColorClass =
                  "bg-emerald-50/80 border-emerald-200/90 text-emerald-700 hover:bg-emerald-100/70 hover:border-emerald-300";
                iconColorClass = "text-emerald-600";
                badgeColorClass = "bg-white text-emerald-700 border-emerald-200";
                break;
              case "blue":
                pillColorClass =
                  "bg-blue-50/80 border-blue-200/90 text-blue-700 hover:bg-blue-100/70 hover:border-blue-300";
                iconColorClass = "text-blue-600";
                badgeColorClass = "bg-white text-blue-700 border-blue-200";
                break;
              case "indigo":
                pillColorClass =
                  "bg-indigo-50/80 border-indigo-200/90 text-indigo-700 hover:bg-indigo-100/70 hover:border-indigo-300";
                iconColorClass = "text-indigo-600";
                badgeColorClass = "bg-white text-indigo-700 border-indigo-200";
                break;
              case "purple":
                pillColorClass =
                  "bg-purple-50/80 border-purple-200/90 text-purple-700 hover:bg-purple-100/70 hover:border-purple-300";
                iconColorClass = "text-purple-600";
                badgeColorClass = "bg-white text-purple-700 border-purple-200";
                break;
              default:
                pillColorClass =
                  "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-2xs";
                iconColorClass = "text-slate-500";
                badgeColorClass = "bg-slate-50 text-slate-700 border-slate-200";
                break;
            }
          }

            return (
            <div
              key={String(item.key)}
              data-testid={
                item.testId ||
                (testIdPrefix ? `${testIdPrefix}-${String(item.key)}` : undefined)
              }
              role={isItemClickable ? "button" : undefined}
              aria-pressed={isItemClickable ? isActive : undefined}
              tabIndex={isItemClickable ? 0 : undefined}
              onClick={isItemClickable ? handleClick : undefined}
              onKeyDown={handleKeyDown}
              title={item.tooltip || item.label}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:py-1 rounded-full text-xs border transition-all duration-150 select-none whitespace-nowrap shrink-0",
                pillColorClass,
                isItemClickable && "cursor-pointer active:scale-95",
                isLoading && "opacity-60 pointer-events-none"
              )}
            >
              {/* Icon */}
              {Icon && (
                <Icon
                  className={cn(
                    "w-3.5 h-3.5 shrink-0 transition-transform",
                    iconColorClass,
                    item.animate && !isZero && "animate-pulse"
                  )}
                />
              )}

              {/* Label */}
              <span className="text-[11px] font-medium tracking-tight">
                {item.label}
              </span>

              {/* Count Badge */}
              <span
                className={cn(
                  "font-mono font-bold text-[11px] sm:text-xs px-1.5 py-0.2 rounded-full border shadow-2xs leading-tight ml-0.5",
                  badgeColorClass
                )}
              >
                {isLoading ? (
                  <span className="inline-block w-4 h-3 bg-slate-200/80 rounded animate-pulse" />
                ) : (
                  displayCount
                )}
              </span>

              {/* Dấu x báo hiệu đang lọc, bấm lần nữa để bỏ lọc */}
              {isActive && isItemClickable && (
                <X className="w-3 h-3 shrink-0 opacity-70" aria-label="Bỏ lọc" />
              )}

              {/* Optional Sublabel */}
              {item.subLabel && (
                <span className="text-[10px] opacity-70 ml-0.5">
                  {item.subLabel}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Extra Action Buttons on Right */}
      {extraActions && (
        <div className="flex items-center gap-1.5 shrink-0 pl-1 border-l border-slate-200">
          {extraActions}
        </div>
      )}
    </div>
  );
}
